import { v3 } from '../math.ts';
import { box, type LevelGeometry } from '../level.ts';
import { makePool, makeShrine, type Arena } from '../arena.ts';
import { VALHALLA_LENGTH, VALHALLA_WALK } from '../constants.ts';

/**
 * The Cathedral — first arena, a gray-box pass of the five-room plan.
 *
 * Eighty metres square and fully enclosed, laid out like the original arenas:
 * tight corridors between larger halls rather than one open field. Corridors are
 * what make walls, arcs and floor signs worth carrying.
 *
 * Rooms: the Nave (centre), the Library (north-west), the Cloister (north-east),
 * the Sitting Room (south-west), the Crypt (south-east).
 */

const W = 80;      // arena width and depth
const H = 9;       // interior height
const T = 1;       // wall thickness
const HALF = W / 2;

/**
 * Valhalla sits well below the arena floor, sealed and unreachable by anything
 * above it. See VALHALLA_* in constants for why it is the length it is.
 */
const VY = -40;    // floor level
const VH = 6;      // interior height
const VHALFW = 5;  // interior half width

function shell(): ReturnType<typeof box>[] {
  return [
    box(0, -0.5, 0, W, 1, W, 'floor', 'shell'),
    box(0, H + 0.5, 0, W, 1, W, 'ceiling', 'shell'),
    box(0, H / 2, -HALF, W, H, T, 'wall', 'shell'),
    box(0, H / 2, HALF, W, H, T, 'wall', 'shell'),
    box(-HALF, H / 2, 0, T, H, W, 'wall', 'shell'),
    box(HALF, H / 2, 0, T, H, W, 'wall', 'shell'),
  ];
}

/** A wall with a doorway gap punched through the middle. */
function wallWithDoor(
  cx: number, cz: number, length: number, axis: 'x' | 'z',
  doorWidth: number, room: string,
): ReturnType<typeof box>[] {
  const side = (length - doorWidth) / 2;
  const offset = doorWidth / 2 + side / 2;
  if (axis === 'x') {
    return [
      box(cx - offset, H / 2, cz, side, H, T, 'wall', room),
      box(cx + offset, H / 2, cz, side, H, T, 'wall', room),
      box(cx, H - 1, cz, doorWidth, 2, T, 'wall', room), // lintel
    ];
  }
  return [
    box(cx, H / 2, cz - offset, T, H, side, 'wall', room),
    box(cx, H / 2, cz + offset, T, H, side, 'wall', room),
    box(cx, H - 1, cz, T, 2, doorWidth, 'wall', room),
  ];
}

/**
 * The penalty box: a straight corridor with the teleporter at the far end.
 *
 * Its length is the whole design. One full fatigue bar buys 50m of sprint, and
 * the walk to the teleporter is 53.5m, so it is deliberately a little further
 * than your legs will carry you — you arrive at the far end walking, whatever
 * you do. That is the penalty, and it is why the number is not round.
 */
function valhallaRoom(): ReturnType<typeof box>[] {
  const halfLen = VALHALLA_LENGTH / 2;
  const outer = VALHALLA_LENGTH + 2 * T;
  const width = VHALFW * 2 + 2 * T;
  return [
    box(0, VY - 0.5, 0, width, 1, outer, 'floor', 'valhalla'),
    box(0, VY + VH + 0.5, 0, width, 1, outer, 'ceiling', 'valhalla'),
    box(0, VY + VH / 2, -halfLen - T / 2, width, VH, T, 'wall', 'valhalla'),
    box(0, VY + VH / 2, halfLen + T / 2, width, VH, T, 'wall', 'valhalla'),
    box(-VHALFW - T / 2, VY + VH / 2, 0, T, VH, outer, 'wall', 'valhalla'),
    box(VHALFW + T / 2, VY + VH / 2, 0, T, VH, outer, 'wall', 'valhalla'),
  ];
}

export const CATHEDRAL_GEOMETRY: LevelGeometry = {
  name: 'Cathedral',
  ambient: 0.42,
  torchIntensity: 55,
  torchRange: 34,
  boxes: [
    ...shell(),
    ...valhallaRoom(),

    // Cross walls dividing the four corner rooms from the central nave.
    ...wallWithDoor(-22, -14, 36, 'x', 5, 'library'),
    ...wallWithDoor(22, -14, 36, 'x', 5, 'cloister'),
    ...wallWithDoor(-22, 14, 36, 'x', 5, 'sitting'),
    ...wallWithDoor(22, 14, 36, 'x', 5, 'crypt'),
    ...wallWithDoor(-14, -22, 36, 'z', 5, 'library'),
    ...wallWithDoor(-14, 22, 36, 'z', 5, 'sitting'),
    ...wallWithDoor(14, -22, 36, 'z', 5, 'cloister'),
    ...wallWithDoor(14, 22, 36, 'z', 5, 'crypt'),

    // Nave colonnade — cover in the middle of the biggest sightlines.
    ...[-8, 0, 8].flatMap((z) =>
      [-6, 6].map((x) => box(x, H / 2, z * 1.4, 1.6, H, 1.6, 'pillar', 'nave')),
    ),

    // Shrine daises.
    box(0, 0.3, -30, 10, 0.6, 10, 'platform', 'order-shrine'),
    box(-26, 0.3, 26, 10, 0.6, 10, 'platform', 'balance-shrine'),
    box(26, 0.3, 26, 10, 0.6, 10, 'platform', 'chaos-shrine'),

    // Crypt gallery — a raised ledge only fliers and Signs of Expulsion reach.
    box(26, 4.5, 8, 20, 0.6, 6, 'platform', 'crypt'),
  ],
  // Free-standing in a gray-box, so nothing to face away from: yaw 0 throughout.
  torches: [
    v3(0, -36, -20), v3(0, -36, 0), v3(0, -36, 20),
    v3(-30, 3, -30), v3(30, 3, -30), v3(-30, 3, 30), v3(30, 3, 30),
    v3(0, 3, -18), v3(0, 3, 18), v3(-18, 3, 0), v3(18, 3, 0),
    v3(0, 3, 0),
  ].map((pos) => ({ pos, yaw: 0 })),
};

export function makeCathedral(): Arena {
  return {
    name: 'Cathedral',
    shrines: [
      makeShrine('order', v3(0, 0.6, -30), v3(0, 0.6, -24)),
      makeShrine('balance', v3(-26, 0.6, 26), v3(-20, 0.6, 22)),
      makeShrine('chaos', v3(26, 0.6, 26), v3(20, 0.6, 22)),
    ],
    pools: [
      makePool('pool_nave', 'The Nave', v3(0, 0, 0)),
      makePool('pool_library', 'The Library', v3(-28, 0, -28)),
      makePool('pool_cloister', 'The Cloister', v3(28, 0, -28)),
      makePool('pool_sitting', 'The Sitting Room', v3(-28, 0, 6)),
      makePool('pool_crypt', 'The Crypt', v3(28, 0, 6)),
      makePool('pool_north', 'North Transept', v3(0, 0, -20)),
      makePool('pool_west', 'West Aisle', v3(-20, 0, 0)),
      makePool('pool_east', 'East Aisle', v3(20, 0, 0)),
    ],
    valhalla: [
      {
        pos: v3(0, VY, -VALHALLA_WALK / 2),
        radius: VHALFW,
        exits: [v3(0, VY, VALHALLA_WALK / 2)],
      },
    ],
    spawns: {
      order: [v3(0, 0.6, -24), v3(4, 0.6, -26), v3(-4, 0.6, -26)],
      balance: [v3(-20, 0.6, 22), v3(-24, 0.6, 20), v3(-16, 0.6, 24)],
      chaos: [v3(20, 0.6, 22), v3(24, 0.6, 20), v3(16, 0.6, 24)],
    },
  };
}
